import buffer from '@turf/buffer'
import { point } from '@turf/helpers'
import type { GeoJsonFeatureCollection } from '@gpx-spa/geo-domain'

/**
 * Matches PostGIS `ST_Buffer(geom, radius, 'quad_segs=9')` described in
 * `gpx-device-api/docs/shipments-geojson.md` (nine segments per quarter-circle → ~36
 * vertices on the ring). Turf `@turf/buffer` passes `steps` to JSTS as quadrant
 * segments for rounded buffers.
 */
export const CIRCLE_BUFFER_QUADRANT_SEGMENTS = 9

/**
 * Expands circle Features to Polygons so GL/Canvas renderers can fill them.
 *
 * - API may return **`geometry`** Polygon (use as-is) or **Point** + `properties.type`,
 *   `center`, `radius` (meters) — clients render a vector circle or this expansion.
 * - See `gpx-device-api/docs/shipments-geojson.md` § Circle stops.
 */
export function expandCircleFeatures(collection: GeoJsonFeatureCollection): GeoJsonFeatureCollection {
  return {
    type: 'FeatureCollection',
    features: collection.features.map(expandCircleFeature),
  }
}

function expandCircleFeature(
  feature: GeoJsonFeatureCollection['features'][0]
): GeoJsonFeatureCollection['features'][0] {
  const p = feature.properties
  if (!p || typeof p !== 'object') return feature
  if ((p as { type?: string }).type !== 'circle') return feature
  if (feature.geometry.type === 'Polygon' || feature.geometry.type === 'MultiPolygon') {
    return feature
  }
  if (feature.geometry.type !== 'Point') return feature
  const radiusM = Number((p as { radius?: number }).radius)
  if (!Number.isFinite(radiusM) || radiusM <= 0) return feature
  const center = (p as { center?: [number, number] }).center
  const coords = (feature.geometry as { type: 'Point'; coordinates: [number, number] }).coordinates
  const lng = center?.[0] ?? coords[0]
  const lat = center?.[1] ?? coords[1]
  const buffered = buffer(point([lng, lat]), radiusM, {
    units: 'meters',
    steps: CIRCLE_BUFFER_QUADRANT_SEGMENTS,
  })
  if (!buffered?.geometry) return feature
  return {
    ...feature,
    geometry: buffered.geometry,
    properties: { ...p },
  }
}
